// ============================================================
// BUNDLE COPIES (plan modes-rework, stage 4) - an Always on item or a Solo
// mode taken from the GM's library onto a sheet: COPIED WITH ITS ORIGIN
// (the asset and version), so the Effects tab can say when the library
// holds a newer one, and Update writes over the copy in place - its id
// kept, so whatever is switched on at the table still names it.
//
// A copy carries the ASSET's name: what the library card says is what the
// strip and the drum say.
//
// Pure. Zero React dependency.
// ============================================================
import type { EffectItem, SoloMode } from './soloModes';
import type { Origin } from '../assets/types';

type Bundle = EffectItem | SoloMode;

/** A library item as the Effects tab offers it: its asset (id, version, name) and its bundle. */
export interface Offered<B extends Bundle> {
  asset: { id: string; version: number; name: string };
  content: B;
}

const originOf = (item: Offered<Bundle>): Origin => ({ assetId: item.asset.id, version: item.asset.version });

/** The bundle as a sheet takes it: the asset's name, and where it came from. */
export const copyOf = <B extends Bundle>(item: Offered<B>): B => ({
  ...item.content,
  name: item.asset.name,
  origin: originOf(item),
});

/** An id no bundle of this list has: the one asked for, or it with a number after it. */
function freeId(list: readonly Bundle[], id: string): string {
  const taken = new Set(list.map((b) => b.id));
  let n = 1;
  let out = id;
  while (taken.has(out)) out = `${id}-${++n}`;
  return out;
}

/** The list with this library item copied in: over its earlier copy (id kept), or added under an id of its own. */
export function withCopy<B extends Bundle>(list: readonly B[], item: Offered<B>): B[] {
  const copy = copyOf(item);
  const at = list.findIndex((b) => b.origin?.assetId === item.asset.id);
  if (at >= 0) return list.map((b, i) => (i === at ? { ...copy, id: b.id } : b));
  return [...list, { ...copy, id: freeId(list, copy.id) }];
}

/** The library item newer than this copy, if any; none for a bundle made on the sheet. */
export function newerOf<B extends Bundle>(bundle: Bundle, items: readonly Offered<B>[]): Offered<B> | null {
  const item = bundle.origin && items.find((i) => i.asset.id === bundle.origin!.assetId);
  return item && item.asset.version > bundle.origin!.version ? item : null;
}

/** The list without the bundle of this id. */
export const withoutBundle = <B extends Bundle>(list: readonly B[], id: string): B[] => list.filter((b) => b.id !== id);
